import React, { useState, useCallback, useMemo, useEffect } from "react";

import {
  ActionIcon,
  Tooltip,
  Modal,
  Stack,
  TextInput,
  Textarea,
  Group,
  Button,
  useMantineTheme,
  Paper,
  Text,
  Code,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconBookmark } from "@tabler/icons-react";

import { useTrackerAuth } from "components/AuthProvider";
import { BENCHMARK_DATASET } from "constants/enums";
import { SavedBenchmarkJob } from "lib/shared/benchmarking/savedJobs";
import {
  isSavedJobDuplicate,
  getSavedJobNavigationUrl,
} from "lib/shared/benchmarking/savedJobsUtils";

interface SaveBenchmarkButtonProps {
  dataset: BENCHMARK_DATASET;
  jobTitle: string;
  location?: string;
  searchParams?: Record<string, any>;
  benchmarkData: {
    percentiles: Record<string, number>;
    dataPoints: number;
    jobCode?: string;
    matchRating?: string | number;
    educationDegree?: string;
    experienceYearMin?: number;
    experienceYearMax?: number;
    raw?: any;
  };
}

export function SaveBenchmarkButton({
  dataset,
  jobTitle,
  location,
  searchParams = {},
  benchmarkData,
}: SaveBenchmarkButtonProps) {
  const theme = useMantineTheme();
  const { user } = useTrackerAuth();
  const [notesModalOpened, { open: openNotesModal, close: closeNotesModal }] = useDisclosure(false);
  const [notes, setNotes] = useState("");
  const [internalJobName, setInternalJobName] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [savedJobs, setSavedJobs] = useState<SavedBenchmarkJob[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasFetched, setHasFetched] = useState(false);

  // Fetch saved jobs on mount - only once per component lifecycle
  useEffect(() => {
    if (!user || hasFetched) {
      setIsLoading(false);
      return;
    }

    const abortController = new AbortController();

    fetch(`/api/benchmarking/saved-jobs?dataset=${dataset}`, {
      signal: abortController.signal,
    })
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        if (!abortController.signal.aborted) {
          setSavedJobs(data.savedJobs || []);
          setHasFetched(true);
          setIsLoading(false);
        }
      })
      .catch((error) => {
        if (error.name !== "AbortError") {
          setSavedJobs([]);
          setHasFetched(true);
          setIsLoading(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [user, dataset, hasFetched]);

  const isSaved = useMemo(() => {
    return savedJobs.some((job) =>
      isSavedJobDuplicate(job, dataset, jobTitle, location, searchParams),
    );
  }, [savedJobs, dataset, jobTitle, location, searchParams]);

  const handleSaveBenchmarkJob = useCallback(
    (e: React.MouseEvent) => {
      // Stop propagation to prevent parent click handlers (like table row click)
      e.stopPropagation();

      if (!user) {
        notifications.show({
          title: "Error",
          message: "You must be logged in to save jobs.",
          color: "red",
        });
        return;
      }

      if (isSaved) {
        notifications.show({
          title: "This benchmark is already saved",
          message:
            "You can view and manage your saved benchmarks from the 'Saved benchmarks' page in the left navigation.",
          color: "blue",
          autoClose: 5000,
        });
        return;
      }

      setInternalJobName(jobTitle);
      openNotesModal();
    },
    [user, isSaved, openNotesModal, jobTitle],
  );

  const handleCancelSave = useCallback(() => {
    closeNotesModal();
    setNotes("");
    setInternalJobName("");
  }, [closeNotesModal]);

  const handleConfirmSave = useCallback(async () => {
    setIsSaving(true);

    try {
      const response = await fetch("/api/benchmarking/saved-jobs", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          dataset: dataset,
          jobTitle: internalJobName.trim() || jobTitle || "Unknown Job",
          location: location,
          searchParams: {
            ...searchParams,
            notes: notes || undefined,
            originalJobTitle: jobTitle,
          },
          benchmarkData: {
            jobCode: benchmarkData.jobCode,
            jobTitle: jobTitle || "Unknown Job",
            location: location,
            percentiles: benchmarkData.percentiles,
            dataPoints: benchmarkData.dataPoints,
            matchRating: benchmarkData.matchRating,
            educationDegree: benchmarkData.educationDegree,
            experienceYearMin: benchmarkData.experienceYearMin,
            experienceYearMax: benchmarkData.experienceYearMax,
            raw: benchmarkData.raw,
          },
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to save job");
      }

      notifications.show({
        title: "Success",
        message: "Job saved successfully!",
        color: "teal",
      });

      // Refresh saved jobs
      const updatedResponse = await fetch(`/api/benchmarking/saved-jobs?dataset=${dataset}`);
      const updatedData = await updatedResponse.json();
      setSavedJobs(updatedData.savedJobs || []);

      closeNotesModal();
      setNotes("");
      setInternalJobName("");
    } catch (error) {
      notifications.show({
        title: "Error",
        message: "Failed to save job. Please try again.",
        color: "red",
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    dataset,
    jobTitle,
    location,
    searchParams,
    benchmarkData,
    notes,
    internalJobName,
    closeNotesModal,
  ]);

  // Don't show saved state until we've loaded the data
  const showSavedState = !isLoading && isSaved;

  // Generate preview URL for debugging
  const previewUrl = useMemo(() => {
    if (!notesModalOpened) return "";

    const mockJob: SavedBenchmarkJob = {
      id: "preview",
      dataset: dataset,
      jobTitle: internalJobName.trim() || jobTitle || "Unknown Job",
      location: location,
      searchParams: {
        ...searchParams,
        notes: notes || undefined,
        originalJobTitle: jobTitle,
      },
      createdAt: new Date(),
      updatedAt: new Date(),
      companyId: 0, // Mock value for preview
      savedById: "", // Mock value for preview
      benchmarkData: {
        jobTitle: internalJobName.trim() || jobTitle || "Unknown Job",
        percentiles: {},
        dataPoints: 0,
      }, // Mock value for preview
      isBlended: false,
      blendedJobIds: [],
    };

    return getSavedJobNavigationUrl(mockJob);
  }, [dataset, jobTitle, location, searchParams, notes, internalJobName, notesModalOpened]);

  // Also show current URL for comparison
  const currentUrl = typeof window !== "undefined" ? window.location.pathname : "";

  // Parse URL parameters for comparison
  const urlComparison = useMemo(() => {
    if (!notesModalOpened || !currentUrl || !previewUrl) return null;

    // Extract parameter parts from URLs
    const currentParts = currentUrl.split("/").pop()?.split("&") || [];
    const previewParts = previewUrl.split("/").pop()?.split("&") || [];

    // Parse into key-value pairs
    const parseParams = (parts: string[]) => {
      const params: Record<string, string> = {};
      parts.forEach((part) => {
        const [key, value] = part.split("=");
        if (key) params[key] = value || "";
      });
      return params;
    };

    const currentParams = parseParams(currentParts);
    const previewParams = parseParams(previewParts);

    // Find differences
    const allKeys = new Set([...Object.keys(currentParams), ...Object.keys(previewParams)]);
    const differences: Array<{
      key: string;
      current: string | undefined;
      preview: string | undefined;
      status: "added" | "removed" | "changed" | "same";
    }> = [];

    allKeys.forEach((key) => {
      const current = currentParams[key];
      const preview = previewParams[key];

      let status: "added" | "removed" | "changed" | "same";
      if (!current && preview) status = "added";
      else if (current && !preview) status = "removed";
      else if (current !== preview) status = "changed";
      else status = "same";

      differences.push({ key, current, preview, status });
    });

    return differences.sort((a, b) => {
      // Sort by status: removed first, then changed, then same, then added
      const order = { removed: 0, changed: 1, same: 2, added: 3 };
      return order[a.status] - order[b.status];
    });
  }, [currentUrl, previewUrl, notesModalOpened]);

  return (
    <>
      <Tooltip label={showSavedState ? "Already saved" : "Save benchmark"}>
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          onClick={handleSaveBenchmarkJob}
          disabled={isSaving || isLoading}
        >
          <IconBookmark
            size={20}
            fill={showSavedState ? theme.colors.bay[6] : "none"}
            color={theme.colors.gray[7]}
          />
        </ActionIcon>
      </Tooltip>

      <Modal
        opened={notesModalOpened}
        onClose={handleCancelSave}
        title="Save Benchmark Job"
        centered
        size="lg"
      >
        <Stack>
          <TextInput
            label="Job title"
            placeholder="Enter a name for this job"
            value={internalJobName}
            onChange={(e) => setInternalJobName(e.currentTarget.value)}
            description="This is how the job will appear in your saved benchmarks"
            required
          />

          <Textarea
            label="Notes (optional)"
            placeholder="Add any notes about this benchmark..."
            value={notes}
            onChange={(e) => setNotes(e.currentTarget.value)}
            rows={4}
          />

          {/* Debug URL Preview - Hidden by default, set to true for debugging */}
          {false && (
            <Paper p="md" withBorder bg="gray.0">
              <Stack gap="xs">
                <Text size="sm" fw={600} c="dimmed">
                  Debug: URL Preview
                </Text>

                <div>
                  <Text size="xs" c="dimmed" mb={4}>
                    Current URL:
                  </Text>
                  <Code block style={{ fontSize: 11, wordBreak: "break-all" }}>
                    {currentUrl}
                  </Code>
                </div>

                <div>
                  <Text size="xs" c="dimmed" mb={4}>
                    URL that will be generated when saved:
                  </Text>
                  <Code block style={{ fontSize: 11, wordBreak: "break-all" }}>
                    {previewUrl}
                  </Code>
                </div>

                {/* Parameter Diff */}
                {urlComparison && (urlComparison?.length ?? 0) > 0 && (
                  <div>
                    <Text size="xs" c="dimmed" mb={4}>
                      Parameter Differences:
                    </Text>
                    <Paper p="xs" withBorder>
                      <Stack gap={4}>
                        {urlComparison!.map((diff, index) => (
                          <Group key={index} gap="xs" wrap="nowrap">
                            <Code
                              style={{
                                fontSize: 11,
                                minWidth: 150,
                                color:
                                  diff.status === "removed"
                                    ? "red"
                                    : diff.status === "added"
                                      ? "green"
                                      : diff.status === "changed"
                                        ? "orange"
                                        : "inherit",
                              }}
                            >
                              {diff.key}
                            </Code>
                            {diff.status === "removed" && (
                              <>
                                <Text size="xs" c="red">
                                  ✗ Missing:
                                </Text>
                                <Code style={{ fontSize: 11, color: "red" }}>
                                  {decodeURIComponent(diff.current || "")}
                                </Code>
                              </>
                            )}
                            {diff.status === "added" && (
                              <>
                                <Text size="xs" c="green">
                                  ✓ Added:
                                </Text>
                                <Code style={{ fontSize: 11, color: "green" }}>
                                  {decodeURIComponent(diff.preview || "")}
                                </Code>
                              </>
                            )}
                            {diff.status === "changed" && (
                              <>
                                <Text size="xs" c="orange">
                                  ≠ Changed:
                                </Text>
                                <Code
                                  style={{
                                    fontSize: 11,
                                    color: "red",
                                    textDecoration: "line-through",
                                  }}
                                >
                                  {decodeURIComponent(diff.current || "")}
                                </Code>
                                <Text size="xs">→</Text>
                                <Code style={{ fontSize: 11, color: "green" }}>
                                  {decodeURIComponent(diff.preview || "")}
                                </Code>
                              </>
                            )}
                            {diff.status === "same" && (
                              <>
                                <Text size="xs" c="dimmed">
                                  ✓
                                </Text>
                                <Code style={{ fontSize: 11, color: "gray" }}>
                                  {decodeURIComponent(diff.preview || "")}
                                </Code>
                              </>
                            )}
                          </Group>
                        ))}
                      </Stack>
                    </Paper>
                  </div>
                )}

                <div>
                  <Text size="xs" c="dimmed" mb={4}>
                    Raw Parameters being captured:
                  </Text>
                  <Code block style={{ fontSize: 11 }}>
                    {JSON.stringify(searchParams, null, 2)}
                  </Code>
                </div>
              </Stack>
            </Paper>
          )}

          {/* Hidden data element for E2E tests - Always present */}
          <div
            data-testid="url-debug-data"
            style={{ display: "none" }}
            data-current-url={currentUrl}
            data-preview-url={previewUrl}
            data-json={JSON.stringify({
              currentUrl,
              previewUrl,
              searchParams,
              comparison: urlComparison,
            })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="subtle" color="gray" onClick={handleCancelSave}>
              Cancel
            </Button>
            <Button
              variant="filled"
              onClick={handleConfirmSave}
              loading={isSaving}
              disabled={!internalJobName.trim()}
            >
              Save Job
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
